"use client";

import { useContext, useEffect } from "react";
import { AppContext } from "@/context/AppContext";
import { motion } from "framer-motion";
import { useData } from "@/i18n/LangContext";

export default function ImprintPageClient() {
  const { setActivePage } = useContext(AppContext);
  const { imprint: imprintData } = useData();

  useEffect(() => {
    setActivePage("imprint");
  }, [setActivePage]);

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 100, damping: 12 }
    }
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 120, damping: 14, delay: 0.1 }
    }
  };

  const decorationVariants = {
    hidden: { scaleX: 0 },
    visible: {
      scaleX: 1,
      transition: { duration: 0.5, ease: [0.6, 0.05, 0.01, 0.9], delay: 0.2 }
    }
  };

  const subsectionDecorationVariants = {
    hidden: { scaleX: 0 },
    visible: {
      scaleX: 1,
      transition: { duration: 0.4, ease: [0.6, 0.05, 0.01, 0.9], delay: 0.1 }
    }
  };

  // Hilfsfunktion zur Erkennung und Formatierung von Adressen
  const formatContent = (text: string, index: number, isSubsection = false) => {
    // Überprüfen, ob der Text eine Adresse sein könnte (enthält mehrere Zeilen mit Postleitzahlen, etc.)
    const isAddressBlock = /^\s*[\w\s\d.,]+(,|\n|\r|\r\n)[\w\s\d.,]+(,|\n|\r|\r\n)[\w\s\d.,]+/.test(text) &&
      (text.includes("straße") || text.includes("weg") || text.includes("allee") ||
        text.includes("Str.") || /\d{5}/.test(text));

    if (isAddressBlock) {
      // Formatiere Text als Adresse mit Zeilenumbrüchen statt Absätzen
      return (
        <motion.div
          className="address-block"
          key={index}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: index * 0.03 + 0.5 }}
        >
          {text.split(/\n|\r\n|\r/).map((line, lineIdx) => (
            <div key={lineIdx} className="address-line">{line}</div>
          ))}
        </motion.div>
      );
    } else {
      // Normaler Textabsatz
      return (
        <motion.p
          key={index}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            delay: index * (isSubsection ? 0.03 : 0.05) + (isSubsection ? 0.8 : 0.5)
          }}
        >
          {text}
        </motion.p>
      );
    }
  };

  return (
    <div className={'py-4 md:py-16 transition-all duration-400'}>

      <div className="content-tile backdrop-blur-sm rounded-xl p-6 md:p-8 bg-[rgba(90,0,0,0.2)] backdrop-blur-sm">
        <motion.h1
          className="page-title"
          variants={titleVariants}
        >
          {imprintData.title}
        </motion.h1>

        <motion.div
          className="title-decoration"
          variants={decorationVariants}
          style={{
            height: "3px",
            width: "100px",
            marginBottom: "2rem",
            transformOrigin: "left",
            borderRadius: "2px"
          }}
        />

        {imprintData.sections.map((section, index) => (
          <motion.section
            key={index}
            className="content-section"
            variants={itemVariants}
          >
            <motion.div
              className="section-header"
              whileHover={{ x: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <h2>{section.title}</h2>
              <div className="section-decoration" style={{
                height: "2px",
                width: "60px",
                marginTop: "0.5rem",
                marginBottom: "1rem",
                borderRadius: "2px"
              }} />
            </motion.div>

            {section.content && (
              <motion.div
                className="content-text"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.05 + 0.3 }}
              >
                {section.content.map((text, textIndex) => (
                  formatContent(text, textIndex)
                ))}
              </motion.div>
            )}

            {section.subsections && section.subsections.map((subsection, subIndex) => (
              <motion.div
                key={subIndex}
                className="content-subsection mt-6 mb-8 last:mb-0"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: subIndex * 0.08 + index * 0.1 + 0.3,
                  type: "spring",
                  stiffness: 100
                }}
              >
                <motion.h3
                  className="text-lg font-semibold mb-2"
                  whileHover={{ x: 3 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  {subsection.title}
                </motion.h3>
                <motion.div
                  className="subsection-decoration bg-[#325E4D]"
                  variants={subsectionDecorationVariants}
                  initial="hidden"
                  animate="visible"
                  style={{
                    height: "1px",
                    width: "40px",
                    marginBottom: "1rem",
                    borderRadius: "1px",
                    transformOrigin: "left"
                  }}
                />
                <motion.div
                  className="content-text"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: subIndex * 0.03 + index * 0.08 + 0.4 }}
                >
                  {subsection.content.map((text, textIndex) => (
                    formatContent(text, textIndex, true)
                  ))}
                </motion.div>
              </motion.div>
            ))}
          </motion.section>
        ))}
      </div>
    </div>
  );
}